import React from "react";
import {
    fetchUserPreferences,
    getCurrentUserPreferenceKey,
    saveUserPreference,
} from "../../utils/userPreferences";

/**
 * Eine Mail-Einstellung einer Person (T-249/T-289), gespeichert in der
 * `UserPreference`-Ablage — Muster `useMailAnsicht`, aber ohne Browser-Speicher:
 * Die Werte sind Postfach-Adressen, und ein am geteilten Rechner liegen
 * gebliebener Startwert der vorigen Person hätte deren Postfächer vorbelegt.
 *
 * Alle Hook-Instanzen teilen EINEN Stand: eine Wahl im Einstellungsfenster
 * wirkt sofort in der Glocke bzw. im offenen Verfassen-Fenster. Der Serverwert
 * wird einmal je Seitenaufruf geholt; bis er da ist, gilt der Standard
 * (`geladen: false`). Eine in dieser Sitzung schon getroffene Wahl wirft ein
 * spät eintreffender Serverwert nicht wieder um.
 *
 * `aktiv = false` (nicht angemeldet): nichts laden und den Stand ALLER dieser
 * Einstellungen vergessen — die Glocke hängt schon auf der Login-Seite, und
 * nach einem Wechsel der Person (Abmelden lädt die Seite nicht neu) darf die
 * Wahl der vorigen Person nicht stehen bleiben. Ein Aufrufer genügt dafür:
 * `useNavBadges` meldet den Anmeldezustand für alle.
 */
export interface NutzerEinstellung<T> {
    useEinstellung(aktiv?: boolean): { wert: T; geladen: boolean; setze: (next: T) => void };
    setze(next: T): void;
    /** Nur für Tests: der Modul-Zustand überlebt sonst von Testfall zu Testfall. */
    __zuruecksetzen(): void;
}

/** Alle angelegten Einstellungen — Abmelden vergisst sie gemeinsam. */
const alleVergessen = new Set<() => void>();

export function erstelleNutzerEinstellung<T>(opts: {
    scope: string;
    standard: T;
    normalisiere: (roh: unknown) => T;
}): NutzerEinstellung<T> {
    type Stand = { wert: T; geladen: boolean };
    let stand: Stand = { wert: opts.standard, geladen: false };
    let selbstGewaehlt = false;
    let serverAbfrage: Promise<void> | null = null;
    // Zählt Abfragen; eine Antwort für eine überholte Abfrage wird verworfen.
    let lauf = 0;
    const hoerer = new Set<() => void>();

    const setzeStand = (next: Stand) => {
        stand = next;
        for (const h of Array.from(hoerer)) h();
    };

    const ladeVomServer = () => {
        if (serverAbfrage) return;
        const meinLauf = ++lauf;
        serverAbfrage = fetchUserPreferences([opts.scope])
            .then((werte) => {
                if (meinLauf !== lauf) return;
                if (selbstGewaehlt) return setzeStand({ ...stand, geladen: true });
                // Fehlt der Scope, gilt der Standard — die Person hat nie gewählt.
                setzeStand({ wert: opts.normalisiere(werte[opts.scope]), geladen: true });
            })
            .catch(() => {
                if (meinLauf !== lauf) return;
                // Server nicht erreichbar — der Standard bleibt; das nächste
                // Fenster darf es noch einmal versuchen.
                serverAbfrage = null;
                setzeStand({ ...stand, geladen: true });
            });
    };

    const vergiss = () => {
        lauf++;
        serverAbfrage = null;
        selbstGewaehlt = false;
        if (stand.wert === opts.standard && !stand.geladen) return;
        setzeStand({ wert: opts.standard, geladen: false });
    };
    alleVergessen.add(vergiss);

    const abonniere = (h: () => void) => {
        hoerer.add(h);
        return () => {
            hoerer.delete(h);
        };
    };
    const aktuell = () => stand;

    const setze = (next: T) => {
        selbstGewaehlt = true;
        setzeStand({ wert: next, geladen: true });
        void (async () => {
            try {
                const userKey = await getCurrentUserPreferenceKey();
                if (!userKey) return;
                await saveUserPreference(userKey, opts.scope, next);
            } catch {
                /* best effort — die Wahl gilt in dieser Sitzung trotzdem */
            }
        })();
    };

    return {
        useEinstellung(aktiv = true) {
            const s = React.useSyncExternalStore(abonniere, aktuell, aktuell);
            React.useEffect(() => {
                if (aktiv) ladeVomServer();
                else for (const v of Array.from(alleVergessen)) v();
            }, [aktiv]);
            return { wert: s.wert, geladen: s.geladen, setze };
        },
        setze,
        __zuruecksetzen() {
            lauf++;
            stand = { wert: opts.standard, geladen: false };
            selbstGewaehlt = false;
            serverAbfrage = null;
            hoerer.clear();
        },
    };
}
